import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Info, Settings } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

interface CategoryData {
  category: string;
  value: number;
  color: string;
}

interface AssetsByCategoryCardProps {
  data: CategoryData[];
  className?: string;
}

export function AssetsByCategoryCard({ data = [], className }: AssetsByCategoryCardProps): React.ReactElement {
  const navigate = useNavigate();

  const hasData = Array.isArray(data) && data.length > 0;

  // Calculate max value for scaling
  const maxValue = hasData ? Math.max(...data.map(d => d.value)) : 0;
  const totalValue = data.reduce((sum, d) => sum + d.value, 0);
  const topCategory = hasData ? data.reduce((max, item) => (item.value > max.value ? item : max)) : undefined;

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Handle bar click to navigate to filtered assets page
  const handleBarClick = (category: string) => {
    navigate(`/assets?category=${encodeURIComponent(category)}`);
  };

  if (!hasData) {
    return (
      <Card className={cn('relative overflow-hidden border-2 shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black', className)}>
        <CardHeader className="relative">
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Assets by Category</CardTitle>
              <CardDescription>Asset value distribution across categories</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">No category data available.</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('relative overflow-hidden border-2 shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black', className)}>

      
      <CardHeader className="relative">
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Assets by Category</CardTitle>
            <CardDescription>Asset value distribution across categories</CardDescription>
          </div>
          <div className="flex gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-background/80">
                    <Info className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">
                    This chart shows the total asset value distributed across different categories. 
                    Each bar represents the cumulative value of all assets in that category.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-background/80">
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Bar Chart */}
          <div className="space-y-4">
            {data.map((item, index) => {
              const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
              
              return (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div 
                        className="h-3 w-3 rounded-full" 
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="font-medium">{item.category}</span>
                    </div>
                    <span className="font-semibold">{formatCurrency(item.value)}</span>
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div 
                          className="relative h-8 w-full overflow-hidden rounded-md bg-muted cursor-pointer hover:ring-2 hover:ring-offset-2 transition-all"
                          style={{ '--tw-ring-color': item.color } as React.CSSProperties}
                          onClick={() => handleBarClick(item.category)}
                        >
                          <div
                            className="h-full transition-all duration-500 ease-out hover:opacity-90"
                            style={{
                              width: `${percentage}%`,
                              backgroundColor: item.color,
                            }}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-sm">Click to view {item.category} assets</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              );
            })}
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Highest Value</p>
              <p className="text-sm font-semibold">
                {topCategory?.category ?? '—'}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Categories</p>
              <p className="text-sm font-semibold">{data.length} types</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
